"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ProjectParseRemediation = exports.ProjectParseFailureCode = exports.NetlistGenerationRemediation = exports.NetlistGenerationFailureCode = exports.RemediationOwner = void 0;
var RemediationOwner;
(function (RemediationOwner) {
    RemediationOwner["Ops"] = "ops";
    RemediationOwner["Customer"] = "customer";
    RemediationOwner["Dev"] = "dev";
})(RemediationOwner || (exports.RemediationOwner = RemediationOwner = {}));
var NetlistGenerationFailureCode;
(function (NetlistGenerationFailureCode) {
    NetlistGenerationFailureCode["AltiumProjectShape"] = "NetlistGeneration_AltiumProjectShape";
    NetlistGenerationFailureCode["ErcErrors"] = "NetlistGeneration_ErcErrors";
    NetlistGenerationFailureCode["AltiumGenerationFailed"] = "NetlistGeneration_AltiumGenerationFailed";
    NetlistGenerationFailureCode["AltiumUnsupportedBuild"] = "NetlistGeneration_AltiumUnsupportedBuild";
    NetlistGenerationFailureCode["AltiumEmptyNetlist"] = "NetlistGeneration_AltiumEmptyNetlist";
    NetlistGenerationFailureCode["AltiumPartialNetlist"] = "NetlistGeneration_AltiumPartialNetlist";
    NetlistGenerationFailureCode["NonNativeNetlist"] = "NetlistGeneration_NonNativeNetlist";
    NetlistGenerationFailureCode["GuestUnavailable"] = "NetlistGeneration_GuestUnavailable";
    NetlistGenerationFailureCode["BridgeDead"] = "NetlistGeneration_BridgeDead";
    NetlistGenerationFailureCode["CommandWedged"] = "NetlistGeneration_CommandWedged";
    NetlistGenerationFailureCode["NoSession"] = "NetlistGeneration_NoSession";
    NetlistGenerationFailureCode["BudgetExhausted"] = "NetlistGeneration_BudgetExhausted";
    NetlistGenerationFailureCode["AltiumNotConfigured"] = "NetlistGeneration_AltiumNotConfigured";
    NetlistGenerationFailureCode["KicadScriptFailed"] = "NetlistGeneration_KicadScriptFailed";
    NetlistGenerationFailureCode["NoBuildFiles"] = "NetlistGeneration_NoBuildFiles";
    NetlistGenerationFailureCode["UnsafePath"] = "NetlistGeneration_UnsafePath";
    NetlistGenerationFailureCode["DuplicateStagePath"] = "NetlistGeneration_DuplicateStagePath";
    NetlistGenerationFailureCode["NoToken"] = "NetlistGeneration_NoToken";
    NetlistGenerationFailureCode["ApiFailed"] = "NetlistGeneration_ApiFailed";
    NetlistGenerationFailureCode["NoGenerator"] = "NetlistGeneration_NoGenerator";
    NetlistGenerationFailureCode["BadStubFile"] = "NetlistGeneration_BadStubFile";
    NetlistGenerationFailureCode["UploadFailed"] = "NetlistGeneration_UploadFailed";
})(NetlistGenerationFailureCode || (exports.NetlistGenerationFailureCode = NetlistGenerationFailureCode = {}));
class NetlistGenerationRemediation {
    static { this.UPLOAD_BY_HAND = 'Meanwhile, the build can proceed with a netlist exported from the CAD tool (or supplied by the customer), uploaded from the netlist column on pipeline ops.'; }
    static { this.ESCALATE = 'Escalate to dev with the build id and the error text below.'; }
    static { this.UNNAMED = {
        owner: RemediationOwner.Dev,
        summary: 'Netlist generation failed in a way the generator did not name.',
        steps: [NetlistGenerationRemediation.ESCALATE, NetlistGenerationRemediation.UPLOAD_BY_HAND],
    }; }
    static { this.BY_CODE = {
        [NetlistGenerationFailureCode.DuplicateStagePath]: {
            owner: RemediationOwner.Ops,
            summary: 'Two of the uploaded files land on the same path, so the generator refused to overwrite either.',
            steps: [
                'Check the build files for duplicates (the same file uploaded twice, or two folders flattened together) and re-upload a clean set.',
                NetlistGenerationRemediation.UPLOAD_BY_HAND,
            ],
        },
        [NetlistGenerationFailureCode.UnsafePath]: {
            owner: RemediationOwner.Ops,
            summary: 'An uploaded file has a path that points outside the build, so the generator refused to stage it.',
            steps: [
                'Re-upload the files without absolute paths or ".." segments in their names.',
                NetlistGenerationRemediation.UPLOAD_BY_HAND,
            ],
        },
        [NetlistGenerationFailureCode.NoBuildFiles]: {
            owner: RemediationOwner.Ops,
            summary: 'The generator found no CAD files on the build to work from.',
            steps: [
                'Check that the upload contains the schematic and project files, and re-upload them if not.',
                NetlistGenerationRemediation.UPLOAD_BY_HAND,
            ],
        },
        [NetlistGenerationFailureCode.AltiumProjectShape]: {
            owner: RemediationOwner.Customer,
            summary: 'The upload has no Altium project file, or more than one, so the generator cannot tell which design to use.',
            steps: [
                'Ask the customer for an upload containing exactly one .PrjPcb and its schematics.',
                NetlistGenerationRemediation.UPLOAD_BY_HAND,
            ],
        },
        [NetlistGenerationFailureCode.ErcErrors]: {
            owner: RemediationOwner.Customer,
            summary: 'The CAD tool refused to export a netlist because the design fails its electrical rules check.',
            steps: [
                'Share the ERC errors below with the customer and ask for corrected files or a netlist they exported themselves.',
                NetlistGenerationRemediation.UPLOAD_BY_HAND,
            ],
        },
        [NetlistGenerationFailureCode.AltiumEmptyNetlist]: {
            owner: RemediationOwner.Customer,
            summary: 'Altium exported an empty netlist: schematic documents the project file lists are not in the upload (the error names them), or the design compiles to nothing.',
            steps: [
                'Ask the customer for the documents named in the error (or the whole project folder, zipped, so nothing is left out), or for a netlist they exported themselves.',
                NetlistGenerationRemediation.UPLOAD_BY_HAND,
            ],
        },
        [NetlistGenerationFailureCode.AltiumPartialNetlist]: {
            owner: RemediationOwner.Dev,
            summary: 'Altium kept exporting a netlist that leaves out a whole schematic sheet, or far fewer components than the schematics carry, so it was not delivered.',
            steps: [
                'The error text names the sheets and components left out on the last attempt; the triage bundle holds the ERC report and tool log of that attempt.',
                NetlistGenerationRemediation.ESCALATE,
                NetlistGenerationRemediation.UPLOAD_BY_HAND,
            ],
        },
        [NetlistGenerationFailureCode.NonNativeNetlist]: {
            owner: RemediationOwner.Dev,
            summary: "OrCAD fell back to its built-in netlist emitter, so the generated netlist can't be trusted as a reference.",
            steps: [NetlistGenerationRemediation.ESCALATE, NetlistGenerationRemediation.UPLOAD_BY_HAND],
        },
        [NetlistGenerationFailureCode.GuestUnavailable]: {
            owner: RemediationOwner.Dev,
            summary: 'The machine that runs the EDA tools was unavailable, and the retries ran out.',
            steps: [NetlistGenerationRemediation.ESCALATE, NetlistGenerationRemediation.UPLOAD_BY_HAND],
        },
        [NetlistGenerationFailureCode.BridgeDead]: {
            owner: RemediationOwner.Dev,
            summary: 'The connection to the EDA tools stopped responding, and the retries ran out.',
            steps: [NetlistGenerationRemediation.ESCALATE, NetlistGenerationRemediation.UPLOAD_BY_HAND],
        },
        [NetlistGenerationFailureCode.CommandWedged]: {
            owner: RemediationOwner.Dev,
            summary: 'The EDA tool stopped responding part-way through the export, and the retries ran out.',
            steps: [NetlistGenerationRemediation.ESCALATE, NetlistGenerationRemediation.UPLOAD_BY_HAND],
        },
        [NetlistGenerationFailureCode.NoSession]: {
            owner: RemediationOwner.Dev,
            summary: 'The EDA tools had no session to run the export in, and the retries ran out.',
            steps: [NetlistGenerationRemediation.ESCALATE, NetlistGenerationRemediation.UPLOAD_BY_HAND],
        },
        [NetlistGenerationFailureCode.BudgetExhausted]: {
            owner: RemediationOwner.Dev,
            summary: 'The export ran out of time on every attempt.',
            steps: [NetlistGenerationRemediation.ESCALATE, NetlistGenerationRemediation.UPLOAD_BY_HAND],
        },
        [NetlistGenerationFailureCode.AltiumGenerationFailed]: {
            owner: RemediationOwner.Dev,
            summary: 'Altium failed to compile the project, and the retries ran out.',
            steps: [NetlistGenerationRemediation.ESCALATE, NetlistGenerationRemediation.UPLOAD_BY_HAND],
        },
        [NetlistGenerationFailureCode.KicadScriptFailed]: {
            owner: RemediationOwner.Dev,
            summary: 'The KiCad export script failed, and the retries ran out.',
            steps: [NetlistGenerationRemediation.ESCALATE, NetlistGenerationRemediation.UPLOAD_BY_HAND],
        },
        [NetlistGenerationFailureCode.AltiumUnsupportedBuild]: {
            owner: RemediationOwner.Dev,
            summary: 'The installed Altium build cannot run the export.',
            steps: [NetlistGenerationRemediation.ESCALATE, NetlistGenerationRemediation.UPLOAD_BY_HAND],
        },
        [NetlistGenerationFailureCode.AltiumNotConfigured]: {
            owner: RemediationOwner.Dev,
            summary: 'Altium generation is not configured on the worker.',
            steps: [NetlistGenerationRemediation.ESCALATE, NetlistGenerationRemediation.UPLOAD_BY_HAND],
        },
        [NetlistGenerationFailureCode.NoToken]: {
            owner: RemediationOwner.Dev,
            summary: "The generator has no credentials for Cadstrom's API.",
            steps: [NetlistGenerationRemediation.ESCALATE, NetlistGenerationRemediation.UPLOAD_BY_HAND],
        },
        [NetlistGenerationFailureCode.NoGenerator]: {
            owner: RemediationOwner.Dev,
            summary: 'The worker has no generator for this CAD tool, although generation was started for it.',
            steps: [NetlistGenerationRemediation.ESCALATE, NetlistGenerationRemediation.UPLOAD_BY_HAND],
        },
        [NetlistGenerationFailureCode.BadStubFile]: {
            owner: RemediationOwner.Dev,
            summary: "The worker's stub netlist is misconfigured.",
            steps: [NetlistGenerationRemediation.ESCALATE, NetlistGenerationRemediation.UPLOAD_BY_HAND],
        },
        [NetlistGenerationFailureCode.ApiFailed]: {
            owner: RemediationOwner.Dev,
            summary: "The generator could not read the build's files from Cadstrom's API.",
            steps: [NetlistGenerationRemediation.ESCALATE, NetlistGenerationRemediation.UPLOAD_BY_HAND],
        },
        [NetlistGenerationFailureCode.UploadFailed]: {
            owner: RemediationOwner.Dev,
            summary: 'The generator produced a netlist but could not upload it to the build.',
            steps: [NetlistGenerationRemediation.ESCALATE, NetlistGenerationRemediation.UPLOAD_BY_HAND],
        },
    }; }
    static { this.CODE_PREFIX = /^(NetlistGeneration_[A-Za-z]+):/; }
    static codeOf(failure) {
        if (failure.type !== undefined && NetlistGenerationRemediation.isCode(failure.type)) {
            return failure.type;
        }
        const prefix = NetlistGenerationRemediation.CODE_PREFIX.exec(failure.message ?? '')?.[1];
        return prefix !== undefined && NetlistGenerationRemediation.isCode(prefix) ? prefix : undefined;
    }
    static forCode(code) {
        return code === undefined ? NetlistGenerationRemediation.UNNAMED : NetlistGenerationRemediation.BY_CODE[code];
    }
    static isCode(value) {
        return Object.values(NetlistGenerationFailureCode).includes(value);
    }
}
exports.NetlistGenerationRemediation = NetlistGenerationRemediation;
var ProjectParseFailureCode;
(function (ProjectParseFailureCode) {
    ProjectParseFailureCode["AltiumProjectListsNoSheets"] = "ProjectParse_AltiumProjectListsNoSheets";
    ProjectParseFailureCode["AltiumProjectFileMissing"] = "ProjectParse_AltiumProjectFileMissing";
})(ProjectParseFailureCode || (exports.ProjectParseFailureCode = ProjectParseFailureCode = {}));
class ProjectParseRemediation {
    static { this.UNNAMED = {
        owner: RemediationOwner.Ops,
        summary: "The build's project files could not be parsed into a project model.",
        steps: [
            'Check that the upload contains the complete design in a supported CAD format, and re-upload it if not.',
            'If the files look right, escalate to dev with the build id and the error text.',
        ],
    }; }
    static { this.BY_CODE = {
        [ProjectParseFailureCode.AltiumProjectListsNoSheets]: {
            owner: RemediationOwner.Customer,
            summary: 'The Altium project file lists no schematic sheets, and the upload gives the parser nothing to use in their place.',
            steps: [
                'Ask the customer to open the project in Altium, add the schematics to it, save, and upload the saved .PrjPcb together with every schematic (and the .PrjPcbStructure file when the project is hierarchical).',
            ],
        },
        [ProjectParseFailureCode.AltiumProjectFileMissing]: {
            owner: RemediationOwner.Customer,
            summary: 'The upload has no Altium project file and no readable schematic to use in its place.',
            steps: ['Ask the customer for an upload containing the .PrjPcb project file and its schematics.'],
        },
    }; }
    static { this.CODE_PREFIX = /^(ProjectParse_[A-Za-z]+):/; }
    static codeOf(reason) {
        const prefix = ProjectParseRemediation.CODE_PREFIX.exec(reason)?.[1];
        return prefix !== undefined && ProjectParseRemediation.isCode(prefix) ? prefix : undefined;
    }
    static guidance(reason) {
        const code = reason === undefined ? undefined : ProjectParseRemediation.codeOf(reason);
        return code === undefined ? ProjectParseRemediation.UNNAMED : ProjectParseRemediation.BY_CODE[code];
    }
    static isCode(value) {
        return Object.values(ProjectParseFailureCode).includes(value);
    }
}
exports.ProjectParseRemediation = ProjectParseRemediation;
//# sourceMappingURL=remediation.js.map